import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi, useUser } from '@features/user';
import { Box, Modal, Slide, Step, StepLabel, Stepper } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { LogoutButton } from '../../../components/Buttons/LogoutButton';
import LanguageAndNotePreferencesForm from '../../../components/LanguageAndNotePreferencesForm';
import { UserInfoForm } from '../../../components/UserInfoForm';
import { useUIState } from '../../../context/uiState';
import { trackEvent } from '../../../utils/analytics_utils';

import { OnboardingWelcomeStep } from './OnboardingWelcomeStep';

export const OnboardingView: React.FC = () => {
  const { completeSignup, userState } = useUser();
  const { state } = useUIState();
  const { platform } = state;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { onboarding: onboardingMetrics } = useEventTracking();
  const [activeStep, setActiveStep] = useState<number>(userState?.userInfo ? 1 : 0);
  const [contentKey, setContentKey] = useState<number>(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const maxSteps = 3;

  // Scroll to top when step changes
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [activeStep]);

  // Track onboarding started when component mounts
  useEffect(() => {
    onboardingMetrics.started({
      initial_step: activeStep === 0 ? 'user_info' : 'preferences',
      has_existing_profile: !!userState?.userInfo,
      platform: platform,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only runs once on mount

  const handleNext = (): void => {
    if (activeStep < maxSteps - 1) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setContentKey(prevKey => prevKey + 1); // Trigger re-render with new key
    }
    // Note: Step 3 (OnboardingWelcomeStep) handles completion via its CTAs
  };

  const onCompleteOnboarding = (location: string = '/'): void => {
    // Track onboarding completion
    onboardingMetrics.completed({
      completion_path: location,
      steps_completed: activeStep + 1,
    });

    completeSignup();
    navigate(location);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Conditional rendering based on activeStep
  const getStepContent = (step: number): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <UserInfoForm
            onSuccessfulFormSubmission={() => {
              // Invalidate related caches after user info update during onboarding
              dispatch(userApi.util.invalidateTags(['Templates', 'TroubleWords', 'Tutorials']));

              trackEvent({
                event: 'signupConfirm',
                platform: platform,
              });
              trackEvent({
                event: 'user_info_form_completed',
                platform: platform,
              });
              handleNext();
            }}
          />
        );
      case 1:
        return (
          <LanguageAndNotePreferencesForm
            onSuccessfulFormSubmission={() => {
              onboardingMetrics.preferencesSet({
                is_final_step: false,
              });

              // Keep legacy tracking for backward compatibility
              trackEvent({
                event: 'onboarding_language_preferences_screen_complete',
                platform: platform,
              });

              handleNext();
            }}
          />
        );

      case 2:
        return <OnboardingWelcomeStep onCompleteOnboarding={onCompleteOnboarding} />;

      default:
        return <div>Unknown step</div>;
    }
  };

  const HorizontalTimeline = () => {
    const steps = ['Get Started', 'Basic Settings', 'Your JotPsych Access'];
    return (
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          '& .MuiStepConnector-line': {
            borderTopWidth: 3,
          },
          '& .MuiStepLabel-label': {
            fontSize: '0.95rem',
            fontWeight: 500,
            display: { xs: 'none', sm: 'block' },
          },
        }}
      >
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  };

  return (
    <>
      <Modal
        open={true}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
        }}
      >
        <Box
          ref={scrollContainerRef}
          data-testid="onboarding-scroll"
          sx={{
            width: '100%',
            minWidth: 0,
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fafafa',
            position: 'relative',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: { xs: 4, sm: 6, md: 8 },
              pb: 4,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '800px',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '800px',
                  mx: 'auto',
                  flexShrink: 0,
                  mb: 4,
                }}
              >
                <HorizontalTimeline />
              </Box>
              <Slide
                id="slide"
                direction="left"
                in={true}
                mountOnEnter
                unmountOnExit
                key={contentKey}
                style={{ transformOrigin: 'right' }}
              >
                <Box
                  sx={{
                    width: '100%',
                    flex: 1,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: { xs: 3, md: 4 },
                  }}
                >
                  {getStepContent(activeStep)}
                </Box>
              </Slide>
            </Box>
          </Box>

          {/* Bottom buttons container */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 4,
              pb: 4,
              mt: 'auto',
            }}
          >
            <LogoutButton size="small" />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
